// src/pages/ServiceMusicTemplatesPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, Calendar, List, Plus, Save } from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';
import Navigation from '../components/Navigation/Navigation';
import EditTemplateModal from '../components/serviceMusicTemplates/EditTemplateModal';
import { useTemplateSync } from '../contexts/TemplateSyncContext';
import apiClient from '../utils/apiClient';
import './ServiceMusicTemplatesPage.css';

interface LiturgicalYear {
  liturgical_year: string;
}

interface Season {
  season: string;
}

interface Church {
  id: number;
  name: string;
}

interface ExistingTemplate {
  season: string;
  liturgical_year: string;
  church_id?: number | null;
  church_name?: string;
  song_count: number;
  last_updated: string;
  created_at: string;
  song_titles: string;
  composers: string;
}

interface TemplateDetail {
  template_id: number;
  season: string;
  liturgical_year: string;
  song_id: number;
  song_placement: string;
  song_title: string;
  composer_name: string;
  lyricist_name: string;
  tune_name: string;
  church_id?: number | null;
  church_name?: string;
}

const ServiceMusicTemplatesPage: React.FC = () => {
  // Get template sync context to notify planning page of updates
  const { notifyTemplateUpdate } = useTemplateSync();

  const [liturgicalYears, setLiturgicalYears] = useState<LiturgicalYear[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [churches, setChurches] = useState<Church[]>([]);
  const [existingTemplates, setExistingTemplates] = useState<ExistingTemplate[]>([]);

  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [selectedChurch, setSelectedChurch] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<TemplateDetail[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const [showPentecostSplitModal, setShowPentecostSplitModal] = useState(false);
  const [pentecostServices, setPentecostServices] = useState<any[]>([]);
  const [summerEndService, setSummerEndService] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [yearsResponse, templatesResponse, churchesResponse] = await Promise.all([
        apiClient.get('/service-music-templates/liturgical-years'),
        apiClient.get('/service-music-templates/templates'),
        apiClient.get('/users/churches')
      ]);

      console.log('Liturgical years from API:', yearsResponse.data.data);
      console.log('Existing templates from API:', templatesResponse.data.data);
      console.log('Churches from API:', churchesResponse.data);
      setLiturgicalYears(yearsResponse.data.data);
      setChurches(churchesResponse.data);

      // Sort templates liturgically before setting them
      const sortedTemplates = sortTemplatesLiturgically(templatesResponse.data.data);
      console.log('Templates after liturgical sorting:', sortedTemplates);
      setExistingTemplates(sortedTemplates);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load liturgical data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedYear) {
      loadSeasonsForYear(selectedYear);
    }
  }, [selectedYear]);

  const loadSeasonsForYear = async (year: string) => {
    try {
      const response = await apiClient.get(`/service-music-templates/seasons/${year}`);
      setSeasons(response.data.data);
    } catch (err) {
      console.error('Error loading seasons:', err);
      setError('Failed to load seasons for selected year');
    }
  };

  const handleNewTemplate = async () => {
    if (!selectedYear || !selectedSeason) {
      alert('Please select both liturgical year and season');
      return;
    }
    
    // Check if Pentecost season is selected - offer to split into Summer/Fall
    if (selectedSeason === 'Pentecost') {
      await loadPentecostServices();
      setShowPentecostSplitModal(true);
      return;
    }
    
    try {
      setLoading(true);
      
      // Check the template creation status
      console.log('Checking status for:', { selectedSeason, selectedYear, selectedChurch });
      const encodedSeason = encodeURIComponent(selectedSeason);
      const encodedYear = encodeURIComponent(selectedYear);
      let url = `/service-music-templates/check-status/${encodedSeason}/${encodedYear}`;
      if (selectedChurch !== null) {
        url += `?churchId=${selectedChurch}`;
      }
      console.log('API URL:', url);
      const response = await apiClient.get(url);
      const status = response.data;
      console.log('Template creation status response:', status);
      
      if (status.scenario === 'template_exists') {
        // Scenario 2A: Template already exists
        alert(status.message);
        return;
      } else if (status.scenario === 'service_music_exists') {
        // Scenario 2B: No template but service music exists
        setConflictMessage(status.message);
        setShowConflictModal(true);
        return; // Wait for user decision
      }
      
      // Scenario 1 (clean_slate) - proceed with template creation
      proceedWithTemplateCreation();
      
    } catch (error) {
      console.error('Error checking template status:', error);
      setError('Failed to check template status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const proceedWithTemplateCreation = () => {
    setSelectedTemplate(`${selectedSeason}|${selectedYear}`);
    setSelectedTemplateDetails([]); // Empty for new template
    setShowEditModal(true);
  };

  const handleKeepExisting = () => {
    setShowConflictModal(false);
    // Keep existing selections and proceed with template creation in append mode
    proceedWithTemplateCreation();
  };

  const handleDeleteExisting = async () => {
    try {
      setShowConflictModal(false);
      setLoading(true);
      
      // Delete existing template music
      await apiClient.delete(`/service-music-templates/service-music/${selectedSeason}/${selectedYear}`);
      
      // Proceed with template creation in clean slate mode
      proceedWithTemplateCreation();
    } catch (error) {
      console.error('Error deleting existing template music:', error);
      setError('Failed to delete existing template music. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPentecostServices = async () => {
    try {
      const response = await apiClient.get(`/service-music-templates/pentecost-services/${selectedYear}`);
      setPentecostServices(response.data.data);
    } catch (error) {
      console.error('Error loading Pentecost services:', error);
      setError('Failed to load Pentecost services. Please try again.');
    }
  };

  const handlePentecostNoSplit = () => {
    setShowPentecostSplitModal(false);
    // Proceed with normal Pentecost template (no split)
    proceedWithTemplateCreation();
  };

  const handlePentecostSplit = () => {
    if (!summerEndService) {
      alert('Please select where summer season ends');
      return;
    }
    
    setShowPentecostSplitModal(false);
    // Start with Summer template creation
    setSelectedTemplate(`Pentecost - Summer|${selectedYear}`);
    setSelectedTemplateDetails([]);
    setShowEditModal(true);
  };

  const handleEditTemplate = () => {
    if (!selectedTemplate) {
      alert('Please select a template to edit');
      return;
    }
    
    setShowEditModal(true);
  };

  const loadTemplateDetails = async (season: string, liturgicalYear: string, churchId?: number | null) => {
    try {
      // Build URL with church_id query parameter if provided
      let url = `/service-music-templates/template/${season}/${liturgicalYear}`;
      if (churchId !== undefined && churchId !== null) {
        url += `?churchId=${churchId}`;
      }
      const response = await apiClient.get(url);
      setSelectedTemplateDetails(response.data.data);
    } catch (err) {
      console.error('Error loading template details:', err);
    }
  };

  const formatYearDisplay = (year: string) => {
    return year.replace('_', ' ');
  };

  // Get liturgical season order (Advent starts the church year)
  const getSeasonOrder = (season: string): number => {
    const seasonMap: { [key: string]: number } = {
      'Advent': 1,
      'Christmas': 2,
      'Epiphany': 3,
      'Lent': 4,
      'Easter': 5,
      'Pentecost': 6,
      'Pentecost - Summer': 6.5,
      'Pentecost - Fall': 7,
    };
    return seasonMap[season] || 99;
  };

  // Extract the end year from liturgical year (e.g., "Year C 2024-2025" -> 2025)
  const extractLiturgicalYear = (liturgicalYear: string): number => {
    const yearMatch = liturgicalYear.match(/(\d{4})-(\d{4})/);
    if (yearMatch) {
      return parseInt(yearMatch[2]);
    }
    const singleYearMatch = liturgicalYear.match(/(\d{4})/);
    return singleYearMatch ? parseInt(singleYearMatch[1]) : 0;
  };

  // Sort templates chronologically (most recent season at top)
  const sortTemplatesLiturgically = (templates: ExistingTemplate[]): ExistingTemplate[] => {
    return [...templates].sort((a, b) => {
      // Combine year and season into a single chronological value
      const yearA = extractLiturgicalYear(a.liturgical_year);
      const yearB = extractLiturgicalYear(b.liturgical_year);
      const chronoA = yearA * 10 + getSeasonOrder(a.season);
      const chronoB = yearB * 10 + getSeasonOrder(b.season);
      // Sort descending (most recent at top)
      return chronoB - chronoA;
    });
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/settings" className="btn btn-secondary">
          <ArrowLeft size={16} className="me-2" />
          Back to Settings
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container mt-4">
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
        <Link to="/settings" className="btn btn-outline-secondary me-3">
          <ArrowLeft size={16} />
        </Link>
        <Music className="me-2 text-warning" size={32} />
        <h1>Service Music Templates</h1>
      </div>

      <div className="row">
        {/* Create New Template - Full Width */}
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <Plus size={20} className="me-2" />
                Create New Template
              </h5>
            </div>
            <div className="card-body">
              {/* Liturgical Year Selection */}
              <div className="mb-3">
                <label htmlFor="liturgicalYear" className="form-label">
                  <Calendar size={16} className="me-2" />
                  Liturgical Year
                </label>
                <select
                  id="liturgicalYear"
                  className="form-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <option value="">Select Liturgical Year</option>
                  {liturgicalYears.map((year) => (
                    <option key={year.liturgical_year} value={year.liturgical_year}>
                      {formatYearDisplay(year.liturgical_year)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Season Selection */}
              <div className="mb-3">
                <label htmlFor="season" className="form-label">
                  <Music size={16} className="me-2" />
                  Liturgical Season
                </label>
                <select
                  id="season"
                  className="form-select"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  disabled={!selectedYear}
                >
                  <option value="">Select Season</option>
                  {seasons.map((season) => (
                    <option key={season.season} value={season.season}>
                      {season.season}
                    </option>
                  ))}
                </select>
              </div>

              {/* Church Selection (Optional) */}
              <div className="mb-3">
                <label htmlFor="church" className="form-label">
                  <Calendar size={16} className="me-2" />
                  Church (optional)
                </label>
                <select
                  id="church"
                  className="form-select"
                  value={selectedChurch?.toString() || ''}
                  onChange={(e) => setSelectedChurch(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!selectedYear || !selectedSeason}
                >
                  <option value="">All Churches</option>
                  {churches.map((church) => (
                    <option key={church.id} value={church.id}>
                      {church.name}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  Leave as "All Churches" to create a template that applies to all churches,
                  or select a specific church to create a church-specific template.
                </small>
              </div>

              {/* Create Button */}
              <button
                className="btn btn-primary w-100"
                onClick={handleNewTemplate}
                disabled={!selectedYear || !selectedSeason}
              >
                <Plus size={16} className="me-2" />
                Create Template
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Summary Section */}
      {existingTemplates.length > 0 && (
        <div className="mt-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <List size={20} className="me-2" />
                Existing Templates Summary
              </h5>
            </div>
            <div className="card-body">
              {/* Dropdown for template selection */}
              <div className="mb-4">
                <label htmlFor="templateSelect" className="form-label">
                  <Calendar size={16} className="me-2" />
                  Select Template to View
                </label>
                <select
                  id="templateSelect"
                  className="form-select"
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    if (e.target.value) {
                      const [season, year, churchIdStr] = e.target.value.split('|');
                      const churchId = churchIdStr ? parseInt(churchIdStr) : null;
                      loadTemplateDetails(season, year, churchId);
                    } else {
                      setSelectedTemplateDetails([]);
                    }
                  }}
                >
                  <option value="">Choose a template...</option>
                  {existingTemplates.map((template) => (
                    <option
                      key={`${template.season}|${template.liturgical_year}|${template.church_id || 'all'}`}
                      value={`${template.season}|${template.liturgical_year}|${template.church_id || ''}`}
                    >
                      {template.season} {formatYearDisplay(template.liturgical_year)}
                      {template.church_name ? ` - ${template.church_name}` : ' - All Churches'}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Display selected template details */}
              {selectedTemplateDetails.length > 0 && (
                <div className="template-details">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      {selectedTemplateDetails[0].season} {formatYearDisplay(selectedTemplateDetails[0].liturgical_year)}
                    </h6>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleEditTemplate}
                    >
                      <Save size={16} className="me-2" />
                      Edit Template
                    </button>
                  </div>
                  <div className="song-list">
                    {selectedTemplateDetails.map((detail, index) => (
                      <div key={detail.template_id} className="mb-2">
                        <strong>{detail.song_placement}:</strong> {detail.song_title}
                        {detail.composer_name && `, ${detail.composer_name}`}
                        {detail.lyricist_name && detail.lyricist_name !== detail.composer_name && ` (text: ${detail.lyricist_name})`}
                        {detail.tune_name && ` - ${detail.tune_name}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Template Modal */}
      {showEditModal && selectedTemplate && (() => {
        // Extract churchId from the selected template string
        // Format for existing templates: "season|year|churchId" (3 parts, churchId can be empty for All Churches)
        // Format for new templates: "season|year" (2 parts, use selectedChurch from form)
        const templateParts = selectedTemplate.split('|');
        const isExistingTemplate = templateParts.length >= 3;
        let templateChurchId: number | null;
        if (isExistingTemplate) {
          // Existing template from dropdown: parse church_id or null for "All Churches"
          templateChurchId = templateParts[2] ? parseInt(templateParts[2]) : null;
        } else {
          // New template: use the church selected in the "Create New" form
          templateChurchId = selectedChurch;
        }
        console.log('[ServiceMusicTemplatesPage] Opening modal with:', {
          selectedTemplate,
          templateParts,
          isExistingTemplate,
          templateChurchId,
          selectedChurch
        });
        return (
        <EditTemplateModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          season={templateParts[0]}
          liturgicalYear={templateParts[1]}
          churchId={templateChurchId}
          churches={churches}
          templateDetails={selectedTemplateDetails}
          cutoffCalendarId={summerEndService ? parseInt(summerEndService) : undefined}
          onUpdate={() => {
            setShowEditModal(false);

            // Notify planning page that templates have changed
            notifyTemplateUpdate();

            // Check if we just finished creating a Summer template and need to create Fall template
            if (selectedTemplate && selectedTemplate.startsWith('Pentecost - Summer') && summerEndService) {
              // Proceed to Fall template creation
              setSelectedTemplate(`Pentecost - Fall|${selectedYear}`);
              setSelectedTemplateDetails([]);
              setShowEditModal(true);
              return;
            }

            // Store current template info before async calls
            const currentTemplate = selectedTemplate;
            const [season, year, churchIdStr] = currentTemplate ? currentTemplate.split('|') : ['', '', ''];
            const templateChurchId = churchIdStr ? parseInt(churchIdStr) : null;

            // Refresh templates list and current template details
            loadInitialData();
            if (currentTemplate && season && year) {
              loadTemplateDetails(season, year, templateChurchId);
            }

            // Reset only the "Create New Template" form fields and Pentecost split state
            // Keep selectedTemplate so user can see updated data
            setSelectedYear('');
            setSelectedSeason('');
            setSelectedChurch(null);
            setSummerEndService('');
            setPentecostServices([]);
          }}
        />
        );
      })()}

      {/* Template Conflict Modal */}
      <Modal show={showConflictModal} onHide={() => setShowConflictModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Music size={24} className="me-2" />
            Existing Template Music Found
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{conflictMessage}</p>
          <p className="mt-3">
            <strong>What would you like to do?</strong>
          </p>
          <ul className="mt-2">
            <li><strong>Keep:</strong> Add new template songs after existing ones</li>
            <li><strong>Delete:</strong> Remove all existing template music and start fresh</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConflictModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteExisting}>
            Delete Existing
          </Button>
          <Button variant="success" onClick={handleKeepExisting}>
            Keep Existing
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pentecost Split Modal */}
      <Modal show={showPentecostSplitModal} onHide={() => setShowPentecostSplitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Music size={24} className="me-2" />
            Pentecost Season Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You've selected the <strong>Pentecost</strong> season for <strong>{selectedYear?.replace('_', ' ')}</strong>.
          </p>
          <p>
            Would you like to split this into separate <strong>Summer</strong> and <strong>Fall</strong> templates?
            This allows you to use different music for the early and later parts of the Pentecost season.
          </p>
          
          {pentecostServices.length > 0 && (
            <div className="mt-3">
              <label htmlFor="summerEndSelect" className="form-label">
                <strong>Summer season ends after which service?</strong>
              </label>
              <select
                id="summerEndSelect"
                className="form-select"
                value={summerEndService}
                onChange={(e) => setSummerEndService(e.target.value)}
              >
                <option value="">Select the last summer service...</option>
                {pentecostServices.map((service) => (
                  <option key={service.calendar_id} value={service.calendar_id}>
                    {service.liturgy_name} - {new Date(service.liturgy_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Services after this date will be part of the Fall template.
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowPentecostSplitModal(false)}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={handlePentecostNoSplit}>
            No, use single Pentecost template
          </Button>
          <Button variant="success" onClick={handlePentecostSplit}>
            Yes, split into Summer & Fall
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </>
  );
};

export default ServiceMusicTemplatesPage;